import styled, { keyframes, css } from 'styled-components';

/* ── paleta do design system ── */
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const RED = 'var(--red)';
const RED_SOFT = 'var(--red-soft)';
const AMBER = 'var(--amber, #d97706)';

const TEAL_GLOW = 'var(--teal-glow)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '420px' };

/* ── animações ── */
const expand = keyframes`
  from { opacity: 0; transform: translateY(-6px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 9999px; }
`;
const shimmer = keyframes`
  0% { background-position: -400px 0; } 100% { background-position: 400px 0; }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideDown = keyframes`
  from { opacity: 0; transform: translate(-50%, -16px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

/* ── botão base ── */
const btnBase = css`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: transform 0.12s ease, filter 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

/* ── Layout base ── */
export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 92vh;
  box-sizing: border-box;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background:
    radial-gradient(1200px 400px at 50% -120px, var(--bg-glow) 0%, transparent 70%),
    ${GRAY_BG};
  padding: 16px;
  @media (max-width: ${bp.tablet}) { padding: 12px; }
  @media (max-width: ${bp.mobile}) { padding: 8px; }
`;

/* cabeçalho da página (hero) */
export const PageHeader = styled.div`
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  max-width: 1200px;
  padding: 22px 26px;
  background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 24px ${TEAL_GLOW};
  animation: ${fadeUp} 0.4s ease;
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }
  &::before { width: 180px; height: 180px; top: -80px; right: -40px; }
  &::after  { width: 120px; height: 120px; bottom: -70px; right: 90px; }
  h1 {
    position: relative;
    z-index: 1;
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  p { position: relative; z-index: 1; margin: 4px 0 0; font-size: 13px; opacity: 0.85; }
  @media (max-width: ${bp.mobile}) { padding: 18px; border-radius: 16px; h1 { font-size: 18px; } }
`;

/* ── Grid dos três painéis (lado a lado no desktop) ── */
export const PanelsGrid = styled.div`
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  gap: 14px;
  @media (max-width: ${bp.desktop}) {
    grid-template-columns: 1fr;
    max-width: 800px;
  }
`;

/* ── Painel branco (reutilizado: Ano Letivo, Regime) ── */
export const ContainerYearControl = styled.div`
  width: 100%;
  height: 100%;
  max-width: 800px;
  box-sizing: border-box;
  text-align: center;
  padding: 26px 24px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  animation: ${fadeUp} 0.35s ease backwards;
  transition: box-shadow 0.18s ease;
  &:hover { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06); }
  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${TEAL_TEXT};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  @media (max-width: ${bp.mobile}) { padding: 20px 16px; border-radius: 14px; }
`;

export const YearBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: ${TEAL_SOFT};
  border: 1px solid var(--teal-soft);
  border-radius: 14px;
  padding: 18px 32px;
  p {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${GRAY_TEXT};
  }
  h1 {
    margin: 0;
    font-size: 3rem;
    font-weight: 800;
    line-height: 1;
    color: ${TEAL_TEXT};
    @media (max-width: ${bp.mobile}) { font-size: 2.2rem; }
  }
`;

export const ButtonNextYear = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  padding: 13px 26px;
  font-size: 14px;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
  @media (max-width: ${bp.small}) { width: 100%; }
`;

/* ── Upload da logo ── */
export const UploadContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 800px;
  box-sizing: border-box;
  text-align: center;
  padding: 26px 24px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  animation: ${fadeUp} 0.35s ease backwards;
  transition: box-shadow 0.18s ease;
  &:hover { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06); }
  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${TEAL_TEXT};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  @media (max-width: ${bp.mobile}) { padding: 20px 16px; border-radius: 14px; }
`;

export const Preview = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  background: ${GRAY_BG};
  padding: 8px;
  box-sizing: border-box;
`;

/* input de arquivo estilizado */
export const FileInput = styled.input`
  width: 100%;
  max-width: 360px;
  font-family: inherit;
  font-size: 13px;
  color: ${GRAY_TEXT};
  cursor: pointer;
  &::file-selector-button {
    ${btnBase}
    background: ${TEAL_SOFT};
    color: ${TEAL_TEXT};
    border: 1px solid var(--teal-soft);
    padding: 9px 16px;
    margin-right: 12px;
    font-size: 13px;
  }
`;

export const ButtonUpload = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  padding: 11px 22px;
  font-size: 14px;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
`;

export const ButtonDelete = styled.button`
  ${btnBase}
  background: ${RED_SOFT};
  color: ${RED};
  border: 1px solid #f1cdcd;
  padding: 11px 22px;
  font-size: 14px;
  width: 100%;
  max-width: 220px;
`;

/* select de regime estilizado */
export const RegimeRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const RegimeSelect = styled.select`
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  font-size: 14px;
  font-family: inherit;
  color: var(--text-2);
  background: ${GRAY_BG};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
`;

export const RegimeButton = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  padding: 11px 24px;
  font-size: 14px;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
`;

/* ── Toast de notificação ── */
export const Notification = styled.div`
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: ${GREEN};
  color: #fff;
  padding: 13px 26px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(29, 139, 90, 0.3);
  font-weight: 700;
  font-size: 14px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${slideDown} 0.3s ease;
  &::before { content: '✓'; font-weight: 900; }
`;

/* ── Overlays / modais (padrão do design system) ── */
const overlayBase = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 30, 38, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
  overscroll-behavior: contain;
  @media (max-width: ${bp.mobile}) { padding: 0; align-items: flex-end; }
`;

export const ModalOverlay = styled.div`${overlayBase}`;
export const ModalOverlayDelete = styled.div`${overlayBase}`;

const modalCard = css`
  background: var(--surface);
  border-radius: 16px;
  padding: 26px 24px;
  width: 100%;
  max-width: 460px;
  box-sizing: border-box;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: ${expand} 0.2s ease;
  max-height: 88vh;
  overflow-y: auto;
  h2 { margin: 0 0 12px; font-size: 18px; color: ${TEAL_TEXT}; }
  h3 { margin: 0 0 12px; font-size: 18px; color: ${AMBER}; }
  p { margin: 0 0 12px; color: ${GRAY_TEXT}; font-size: 14px; line-height: 1.55; text-align: left; }
  @media (max-width: ${bp.mobile}) { border-radius: 18px 18px 0 0; padding: 24px 18px; max-width: 100%; }
`;

export const ModalContent = styled.div`
  ${modalCard}
  /* botões do modal de regime (classes internas) */
  .modal-buttons { display: flex; justify-content: center; gap: 10px; margin-top: 20px; }
  .modal-button {
    ${btnBase}
    padding: 10px 20px;
    font-size: 14px;
  }
  .confirm { background: ${GREEN}; color: #fff; }
  .cancel { background: ${RED}; color: #fff; }
  @media (max-width: ${bp.small}) {
    .modal-buttons { flex-direction: column-reverse; }
    .modal-button { width: 100%; }
  }
`;

export const ModalBox = styled.div`${modalCard}`;

/* botões dos modais de ano letivo (verde/vermelho) */
export const ModalButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  button {
    ${btnBase}
    padding: 10px 22px;
    font-size: 14px;
  }
  button:first-child { background: ${GREEN}; color: #fff; }
  button:last-child  { background: var(--surface-2); color: ${GRAY_TEXT}; border: 1px solid ${GRAY_LINE}; }
  @media (max-width: ${bp.small}) {
    flex-direction: column-reverse;
    button { width: 100%; }
  }
`;

export const ModalButtonsDelete = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  button {
    ${btnBase}
    padding: 10px 22px;
    font-size: 14px;
  }
  button:first-child { background: ${RED}; color: #fff; }
  button:last-child  { background: var(--surface-2); color: ${GRAY_TEXT}; border: 1px solid ${GRAY_LINE}; }
  @media (max-width: ${bp.small}) {
    flex-direction: column-reverse;
    button { width: 100%; }
  }
`;

/* lista de turmas pendentes */
export const TurmaList = styled.ul`
  text-align: left;
  margin: 8px 0 16px;
  padding: 12px 14px;
  list-style: none;
  background: ${RED_SOFT};
  border: 1px solid #f1cdcd;
  border-radius: 11px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
`;

export const TurmaItem = styled.li`
  font-size: 13.5px;
  font-weight: 600;
  color: ${RED};
  display: flex;
  align-items: center;
  gap: 7px;
  &::before { content: '⚠'; }
`;

export const Button = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  padding: 11px 24px;
  font-size: 14px;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
`;

/* ── modal de finalização (com spinner) ── */
export const FinalizingCard = styled.div`
  ${modalCard}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  max-width: 460px;
  p { text-align: center; margin: 0; font-weight: 700; color: ${TEAL_TEXT}; font-size: 15px; }
`;

/* ── Skeleton ── */
const skeletonBase = css`
  background: linear-gradient(90deg, var(--skel-a) 0%, var(--skel-b) 50%, var(--skel-a) 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
  border-radius: 8px;
`;
export const SkeletonPanel = styled.div`
  ${skeletonBase}
  width: 100%;
  max-width: 800px;
  height: ${({ $h }) => $h || '180px'};
  border-radius: 16px;
`;